import type {Point} from '@postermywall/fabricjs-2';
import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {ImageSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';

export type CropTargetItem = ImageItem | ImageSlideItem | VideoItem | VideoSlideItem;

/** The picture the edit image is built on: a downloaded full-size image, or the video item's own <video>. */
export type CropSourceElement = HTMLImageElement | HTMLVideoElement;

/** What is being cropped. Names the GA4 event type and picks the title the crop surfaces show. */
export enum CropMediaType {
  IMAGE = 'image',
  VIDEO = 'video',
}

/**
 * Fixed aspect-ratio presets for the crop frame (width / height). `null` = freeform,
 * "Original" is computed at runtime from the image's natural dimensions.
 */
export const CROP_ASPECT_RATIO = {
  SQUARE: 1,
  FOUR_THREE: 4 / 3,
  NINE_SIXTEEN: 9 / 16,
  SIXTEEN_NINE: 16 / 9,
} as const;

/**
 * Crop-mode UI carries this DOM attribute; a press inside such an element is never the outside click
 * that applies the crop and leaves the mode. The key form suits react-modal's `data` prop.
 */
export const CROP_SURFACE_DATA_KEY = 'crop-surface';
export const CROP_SURFACE_ATTRIBUTE = `data-${CROP_SURFACE_DATA_KEY}`;

/** The ratio the window is locked to (`null` frees it), and whether "Original" chose it - a square picture's own ratio is also Square's. */
export interface CropAspectLock {
  ratio: number | null;
  isOriginal: boolean;
}

/** GA4 value for the preset in force when a crop is applied. */
export const CROP_GA_PRESET_FREE = 'free';
export const CROP_GA_PRESET_ORIGINAL = 'original';
/** A background's window is the page; no preset was picked. */
export const CROP_GA_PRESET_PAGE = 'page';
export const CROP_GA_PRESET_BY_ASPECT_RATIO: ReadonlyMap<number, string> = new Map([
  [CROP_ASPECT_RATIO.SQUARE, 'square'],
  [CROP_ASPECT_RATIO.FOUR_THREE, '4:3'],
  [CROP_ASPECT_RATIO.NINE_SIXTEEN, '9:16'],
  [CROP_ASPECT_RATIO.SIXTEEN_NINE, '16:9'],
]);

/** The part of the picture outside the window sits under a black scrim of this opacity, so the framing reads at a glance. */
export const CROP_SCRIM_COLOR = '#000000';
export const CROP_SCRIM_OPACITY = 0.2;

/** Halvings used to find how far a gesture may go before the window would leave the picture. */
export const CROP_CONTAINMENT_SEARCH_STEPS = 12;

/** Rule-of-thirds guide lines inside the crop window: hairline dashes in screen pixels, whatever the canvas zoom. */
export const CROP_GUIDE_COLOR = 'rgba(238,238,238,0.8)';
export const CROP_GUIDE_LINE_WIDTH_PX = 1;
export const CROP_GUIDE_DASH_LENGTH_PX = 4;

export interface CropWindowSize {
  width: number;
  height: number;
}

/** Rotation slider bounds, in degrees; the slider's filled track runs from the neutral angle to the value. */
export const CROP_ROTATION_MIN_ANGLE = -180;
export const CROP_ROTATION_MAX_ANGLE = 180;
export const CROP_ROTATION_NEUTRAL_ANGLE = 0;

/** Smallest crop window, in source-image pixels, a handle drag may produce. */
export const MIN_CROP_SIZE_PX = 16;

/** How far, in canvas pixels, a press beside the picture may travel before its release stops counting as a click. */
export const CROP_OUTSIDE_CLICK_TOLERANCE_PX = 4;

/** Below this, in poster units or degrees, two frames are the same frame - float residue from a rotation round-trip is not an edit. */
export const CROP_FRAME_EQUALITY_TOLERANCE = 0.01;

/** Relative slack for a window to count as already having a preset's ratio - a stored crop was rounded to whole pixels on apply. */
export const CROP_ASPECT_RATIO_MATCH_TOLERANCE = 0.01;

/** How long a crop-mode notice stays up: the picture could not be downloaded, or the image is still uploading. */
export const CROP_NOTICE_GROWL_INTERVAL_MS = 3000;

/** The eight resize handles, named for the corner or edge each one drags. */
export enum CropHandle {
  TOP_LEFT = 'tl',
  TOP_RIGHT = 'tr',
  BOTTOM_LEFT = 'bl',
  BOTTOM_RIGHT = 'br',
  MIDDLE_LEFT = 'ml',
  MIDDLE_RIGHT = 'mr',
  MIDDLE_TOP = 'mt',
  MIDDLE_BOTTOM = 'mb',
}

/**
 * An on-screen box in poster coordinates (before `posterScale`). `left`/`top` are the rotated box's
 * top-left corner, matching how fabric reports position for PMW's left/top origin.
 */
export interface CropFrame {
  left: number;
  top: number;
  width: number;
  height: number;
  angle: number;
}

/**
 * Both boxes the React overlay draws: the crop window, and the full image behind it. The image box
 * carries the standard selection chrome (corner scale handles, rotation), the window the crop handles.
 */
export interface CropFrames {
  window: CropFrame;
  image: CropFrame;
}

/**
 * The crop while it is being edited, in the "visual" source space: the picture as the user sees it,
 * with the item's flips already applied, origin at its top-left, one unit per source pixel. The
 * window stays where it is on screen; the picture is what turns, scales and slides behind it.
 */
export interface StraightenCropState {
  sourceWidth: number;
  sourceHeight: number;
  /** The source point under the window's centre. */
  windowCentre: Point;
  /** The window's size in source pixels, measured along its own axes. */
  windowWidth: number;
  windowHeight: number;
  /** Degrees the picture is turned about the window's centre. */
  pictureRotation: number;
  /** The window on the canvas: its centre in scene units and its angle, which is the item's angle. */
  windowCentreScene: Point;
  windowAngle: number;
  /** Scene units per source pixel along the window's own x and y axes. */
  scaleX: number;
  scaleY: number;
}

/** Corner handles only - the full image scales proportionally, so it has no edge handles. */
export type CropImageCorner = CropHandle.TOP_LEFT | CropHandle.TOP_RIGHT | CropHandle.BOTTOM_LEFT | CropHandle.BOTTOM_RIGHT;

export const isCropImageCorner = (handle: CropHandle): handle is CropImageCorner => {
  return handle === CropHandle.TOP_LEFT || handle === CropHandle.TOP_RIGHT || handle === CropHandle.BOTTOM_LEFT || handle === CropHandle.BOTTOM_RIGHT;
};
